/**
 * 导入必要的Angular模块和组件
 */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Trail } from '../../dto/trail';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { TrailPropertiesService } from '../../service/trail-properties.service'
import { TrailType } from '../../dto/trail-type';
import { TrailStatus } from '../../dto/trail-status';
import { TrailRollingScheme } from '../../dto/trail-rolling-scheme';
import { FileService } from '../../../../common/file/service/file.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailService } from '../../service/trail.service';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';
import { Customer } from '../../../customer/dto/customer';
import { TrialReportDialogComponent } from '../trial-report-dialog/trial-report-dialog.component';

/**
 * TrailDetailsComponent组件 - 用于显示和编辑试验详情
 */
@Component({
  selector: 'dpi-trail-details',
  imports: [NgIf, FormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule, MatButtonModule,
    MatToolbarModule, MatSelectModule, MatTooltipModule, MatDialogModule],
  templateUrl: './trail-details.component.html',
  styleUrl: './trail-details.component.scss'
})
export class TrailDetailslComponent implements OnInit, OnDestroy {
  // 试验相关数据
  trail: Trail;
  customer: Customer;
  hasManagerRight: boolean = false;
  hasEditorRight: boolean = false; 
  isReadonly: boolean;
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  allTrailTypes: TrailType[];
  allTrailStatus: TrailStatus[];
  allTrailRollingScheme: TrailRollingScheme[];

  selectedTrailType: TrailType;
  selectedTrailStatus: TrailStatus;
  selectedTrailRollingScheme: TrailRollingScheme;

  constructor(
    private notifyService: StateNotifyService,
    private trailService: TrailService,
    private trailPropService: TrailPropertiesService,
    private service: FileService,
    private router: Router) {

    this.trail = this.notifyService.getSelectedTrail();
    this.customer = this.notifyService.getSelectedCustomer();
    
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;

    this.allTrailTypes = this.trailPropService.getAllTrialTypes();
    this.allTrailStatus = this.trailPropService.getAllTrialStatus();
    this.allTrailRollingScheme = this.trailPropService.getAllTrialRollingScheme();

    this.selectedTrailType = this.trailPropService.getTrialType(this.allTrailTypes, this.trail.type);
    this.selectedTrailStatus = this.trailPropService.getTrialStatus(this.allTrailStatus, this.trail.status);
    this.selectedTrailRollingScheme = this.trailPropService.getTrialRollingScheme(this.allTrailRollingScheme, this.trail.rolling_scheme);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model.id);
      });

    this.notifyService.customerSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.customer = model;
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });

    this.hasManagerRight = false;
    if (this.customer.user_role != null) {
      if (this.customer.user_role <= 1) {
        this.hasManagerRight = true;
      }
    }

    this.hasEditorRight = false;
    if (this.customer.user_role != null) {
      if (this.customer.user_role <= 2) {
        this.hasEditorRight = true;
      }
    }
  }

  reload(trailId: number): void {
    this.trailService.getTrialById(trailId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.trail = res;
          this.selectedTrailType = this.trailPropService.getTrialType(this.allTrailTypes, this.trail.type);
          this.selectedTrailStatus = this.trailPropService.getTrialStatus(this.allTrailStatus, this.trail.status);
          this.selectedTrailRollingScheme = this.trailPropService.getTrialRollingScheme(this.allTrailRollingScheme, this.trail.rolling_scheme);
        },
        error: err => { console.log(err); }
      });
  }

  generateTrialReport() {
      const dialogRef = this._dialogConfirm.open(TrialReportDialogComponent, { data: this.trail });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
            if (dialogRef.componentInstance.isSuccessful) {
              let fileName = dialogRef.componentInstance.result?.fileName;
              if (fileName != undefined) {
                this.getGeneratedTrialReport(fileName);
              }
            }
        });
  }

  getGeneratedTrialReport(fileName: string) { // default type "ndt" to download the NDT report, or "dnv" for DNV report
    this.trailService.getGeneratedTrialReport(fileName)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob: any) => {
          // action of file saving
          this.service.saveAs(blob, fileName);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          // show error message to the user
        }
      });
  }

  /*
  getTrialReport(type: string = "ndt") { // default type "ndt" to download the NDT report, or "dnv" for DNV report
    this.trailService.getTrialReport(this.trail.id, type)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob: any) => {
          // action of file saving
          this.service.saveAs(blob, "report.docx");
        },
        error: (error: any) => {
          console.error('Error: ', error);
          // show error message to the user
        }
      });
  }
  */

  onEditModeChange(isReadonly: boolean) {
  // 将传入的只读状态保存到实例变量中
    this.isReadonly = isReadonly;
  // 如果是只读模式
    if (isReadonly) {
    // 设置字段外观为轮廓样式
      this.fieldAppearance = 'outline';
    } else {
    // 否则设置字段外观为填充样式
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  trailTypeChanged() {
    this.trail.type = this.selectedTrailType.id;
    this.isContentChanged = true;
  }

  trailStatusChanged() {
    this.trail.status = this.selectedTrailStatus.id;
    this.isContentChanged = true;
  }

  trailRollingSchemeChanged() {
    this.trail.rolling_scheme = this.selectedTrailRollingScheme.id;
    this.isContentChanged = true;
  }

  // --- CRUD actions ---

  createTrail() {
    this.trailService.createTrial(this.trail.vessel_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateTrail() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.trailService.updateTrial(this.trail)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.trail = model;
          this.notifyService.updateTrailSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteTrail() {
    this.trailService.deleteTrial(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeTrailAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  templateCopyTrial() {
      this.trailService.templateCopyTrial(this.trail.id, this.trail.vessel_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteTrialTemplate() {
    this.trailService.deleteTrialTemplate(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeTrailAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: Trail) {
    this.trail = model;
    this.notifyService.updateTrailSelection(this.trail);
    let listTrail = this.notifyService.getListAllTrails();
    listTrail.push(model);
    this.notifyService.updateListAllTrails(listTrail);
  }

  private removeTrailAfterDelete() {
    let listTrail = this.notifyService.getListAllTrails();
    const index = listTrail.indexOf(this.trail, 0);
    if (index > -1) {
      listTrail.splice(index, 1);
    }
    this.notifyService.updateListAllTrails(listTrail);

    // Select the current 'vessel', then navigate to 'vessel-hub', i.e. to load and display the list of trails after deletion.
    let vesselModel = this.notifyService.getSelectedVessel();
    this.notifyService.updateVesselSelection(vesselModel); //update the same vessel selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['vessel-hub']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole test will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteTrail();
            }
          };
        });
    }
  }

  btnCopyAsTemplateClicked() {
    if (!this.isReadonly && this.hasManagerRight) {
      let message = "Use the current trial as template to create a new trial, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.templateCopyTrial();
            }
          };
        });
    }
  }

  btnDeleteTemplateClicked() {
    if (!this.isReadonly) {
      let message = "The whole trial as a template will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteTrialTemplate();
            }
          };
        });
    }
  }

}
